import React, {useState, useEffect, useCallback} from 'react';
import {View, Platform, ScrollView, Image, Text, Pressable} from 'react-native';
import {StyleSheet} from 'react-native';
import {RNEText, RNEButton, RNEInput} from '../../components';

import {useForm, Controller, useFieldArray} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {colorPalette, colors} from '../../../core/config/color.config';

import {
  MedicationRequestArrayUI,
  MedicationRequestReqBodyUI,
  MedicationRequestStatusEnum,
} from '../../server/types/medical-attachment.types';
import {useToast} from 'react-native-fast-toast';
import {
  useAddPrescription,
  useGetPrescriptions,
  useUpdatePrescription,
} from '../../server/react-query/useMedicalAttachment';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {REACT_APP_BASE_WEB_URL} from '../../server/api/api.config';
import useStores from '../../stores/useStores';
import {width, height, totalSize} from 'react-native-dimension';
import moment from 'moment';
import LightAppLogo from '../../../assets/logo/logo-light.png';
import {useGetConsultationDetail} from '../../server/react-query/useConsultation';
import {Overlay} from 'react-native-elements';
import ConsultationCancelConfirmationModal from '../modal/ConsultationCancelConfirmationModal';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';
import {KeyboardAvoidingView} from 'react-native';

interface AddPrescriptionViewProps {
  basedOnId: string;
  subject: string;
  userId: string;
}

const schema: yup.Schema<MedicationRequestArrayUI> = yup.object().shape({
  prescriptions: yup
    .array()
    .of(
      yup.object().shape({
        // generic: yup.string().trim().required('This field is required.'),
        // brand: yup.string().trim().required('This field is required.'),
        // dose: yup.string().trim().required('This field is required.'),
        // frequency: yup.string().trim().required('This field is required.'),
        // quantity: yup.string().trim().required('This field is required.'),
        generalInstruction: yup
          .string()
          .trim()
          .required('This field is required.'),
      }),
    )
    .required(),
});

const AddPrescriptionView = (props: AddPrescriptionViewProps) => {
  const {authStore} = useStores();
  const toast = useToast();
  const route = useRoute();
  const navigation = useNavigation();

  const {data, isFetching, refetch} = useGetPrescriptions({
    basedOnId: props.basedOnId,
    status: MedicationRequestStatusEnum.DRAFT,
  });

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    trigger,
    formState: {errors, isValid},
  } = useForm<MedicationRequestArrayUI>({
    mode: 'all',
    defaultValues: {
      prescriptions: [
        {
          generic: '',
          brand: '',
          dose: '',
          frequency: '',
          quantity: '',
          generalInstruction: '',
        },
      ],
    },
    resolver: yupResolver(schema),
  });

  const {fields, remove, prepend, append} = useFieldArray({
    control,
    name: 'prescriptions',
  });

  const [patientData, setPatientData] = useState(null);
  const [practitionerData, setPractitionerData] = useState(null);
  const [procedureId, setProcedureId] = useState(null);

  const [isSaveAttachmentModalVisible, setIsSaveAttachmentModalVisible] =
    useState(false);
  const [confirmationModalTitle, setConfirmationModalTitle] = useState('');
  const [confirmationModalType, setConfirmationModalType] = useState('');

  const [basedOnId, setBasedOnId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [status, setStatus] = useState<MedicationRequestStatusEnum>(
    MedicationRequestStatusEnum.DRAFT,
  );

  const {setCanRefresh} = usePullDownRefreshContext();

  const {data: consultationDetails} = useGetConsultationDetail({
    serviceRequestId: props.basedOnId,
  });

  useEffect(() => {
    if (consultationDetails) {
      setPatientData(consultationDetails?.subject);
      setPractitionerData(consultationDetails?.performer);

      setProcedureId(consultationDetails?.procedure?.procedureId);
    }
  }, [consultationDetails]);

  const addMutation = useAddPrescription(basedOnId, status, getValues());

  const updateMutation = useUpdatePrescription(
    groupId,
    basedOnId,
    status,
    getValues(),
    basedOnId,
  );

  const [mutation, setMutation] = useState(addMutation);

  const prependInscription = () => {
    prepend({
      generic: '',
      brand: '',
      dose: '',
      frequency: '',
      quantity: '',
      generalInstruction: '',
    });
  };

  const savePrescription = (requestStatus: MedicationRequestStatusEnum) => {
    setStatus(requestStatus);
    trigger('prescriptions');
    handleSubmit(onSubmit)();
  };

  const getFullName = humanName => {
    const givenNames = Array.isArray(humanName?.given)
      ? humanName.given.join(' ').trim()
      : '';

    const familyName = humanName?.family ?? '';

    return [givenNames, familyName].filter(Boolean).join(' ');
  };

  const getAgeFromDOB = dobString => {
    const dob = new Date(dobString);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const hasBirthdayPassedThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

    if (!hasBirthdayPassedThisYear) {
      age -= 1;
    }

    return age;
  };

  const getSpecialtiesCommaSeparated = (practitionerRole = []) => {
    return practitionerRole
      .map(role => role?.specialty?.text)
      .filter(Boolean) // remove undefined/null
      .join(', ');
  };

  const navProps = {
    procedureId: props.basedOnId,
    subjectId: props.subject,
    receiver: props.subject,
    serviceRequestId: props.basedOnId,
  };

  // REMOVED: useSendMobilePushNotification is no longer used
  // const triggerPushNotification = useSendMobilePushNotification({...});

  const onSubmit = async () => {
    if (isValid) {
      try {
        await mutation.mutateAsync();
        refetch();

        reset();
        mutation.reset();
        navigation.goBack();
       
        // REMOVED: useSendMobilePushNotification is no longer used
        // triggerPushNotification.mutateAsync();

      } catch (error) {
        if (error?.data?.error) {
          toast?.show(error?.data?.error, {type: 'danger'});
        }
      }
    }
  };

  useEffect(() => {
    const params = route.params as any;

    if (params) {
      setBasedOnId(params.basedOnId);
    }
  }, [route.params]);

  useEffect(() => {
    if (data && data.length > 0) {
      const prescriptions: MedicationRequestReqBodyUI[] = [];

      data.map(prescription => {
        const inscription: MedicationRequestReqBodyUI = {
          generic:
            prescription.medicationCodeableConcept.code?.find(
              med => med.system === 'generic-name',
            )?.display || '',
          brand:
            prescription.medicationCodeableConcept.code?.find(
              med => med.system === 'brand-name',
            )?.display || '',
          dose: prescription.dosageInstruction.doseAndRate.doseText,
          frequency: prescription.dosageInstruction.timing.text,
          quantity: prescription.dosageInstruction.doseAndRate.rateText,
          generalInstruction: prescription.note.text,
        };

        prescriptions.push(inscription);
      });

      append(prescriptions);
      setGroupId(data?.[0]?.groupId);
      setMutation(updateMutation);
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (groupId) {
      remove(0);
    }
  }, [groupId, remove]);

  useEffect(() => {
    if (props.basedOnId) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <>
      {Platform.OS === 'web' ? (
        <ScrollView>
          <View>
            <View style={webStyles.formContainer}>
              <View style={webStyles.rightContainer}>
                <RNEText
                  label="Inscription"
                  style={subtitleStyle}
                  variant="title"
                  weight="bold"
                />

                {fields.map((prescription, index) => {
                  return (
                    <View
                      style={webStyles.labelGroupContainer}
                      key={prescription.id}>
                      <View style={webStyles.rowGroupContainer}>
                        <View style={webStyles.inputContainer}>
                          <View style={webStyles.inputLabel}>
                            <RNEText
                              label="Generic"
                              variant="button"
                              color="accent"
                              weight="bold"
                            />
                          </View>
                          <Controller
                            control={control}
                            render={({field: {onChange, onBlur, value}}) => (
                              <RNEInput
                                height={60}
                                placeholder="Enter Generic Name"
                                inputContainerStyle={inputContainerWebStyle}
                                defaultValue={`${prescription.generic}`}
                                onChangeText={text => {
                                  onChange(text);
                                }}
                                errorMessage={
                                  errors.prescriptions?.[index]?.generic
                                    ?.message
                                }
                                variant="shadowed"
                              />
                            )}
                            defaultValue={prescription.generic}
                            name={`prescriptions.${index}.generic`}
                          />
                        </View>

                        <View style={webStyles.inputContainer}>
                          <View style={webStyles.inputLabel}>
                            <RNEText
                              label="Brand"
                              variant="button"
                              color="accent"
                              weight="bold"
                            />
                          </View>
                          <Controller
                            control={control}
                            render={({field: {onChange, onBlur, value}}) => (
                              <RNEInput
                                height={60}
                                placeholder="Enter Brand Name"
                                inputContainerStyle={inputContainerWebStyle}
                                defaultValue={`${prescription.brand}`}
                                onChangeText={text => {
                                  onChange(text);
                                }}
                                errorMessage={
                                  errors.prescriptions?.[index]?.brand?.message
                                }
                                variant="shadowed"
                              />
                            )}
                            defaultValue={prescription.brand}
                            name={`prescriptions.${index}.brand`}
                          />
                        </View>
                      </View>

                      <View style={webStyles.rowGroupContainer}>
                        <View style={webStyles.inputContainer}>
                          <View style={webStyles.inputLabel}>
                            <RNEText
                              label="Dose"
                              variant="button"
                              color="accent"
                              weight="bold"
                            />
                          </View>
                          <Controller
                            control={control}
                            render={({field: {onChange, onBlur, value}}) => (
                              <RNEInput
                                height={60}
                                placeholder="Enter Dosage"
                                inputContainerStyle={inputContainerWebStyle}
                                defaultValue={`${prescription.dose}`}
                                onChangeText={text => {
                                  onChange(text);
                                }}
                                errorMessage={
                                  errors.prescriptions?.[index]?.dose?.message
                                }
                                variant="shadowed"
                              />
                            )}
                            defaultValue={prescription.dose}
                            name={`prescriptions.${index}.dose`}
                          />
                        </View>

                        <View style={webStyles.inputContainer}>
                          <View style={webStyles.inputLabel}>
                            <RNEText
                              label="Frequency"
                              variant="button"
                              color="accent"
                              weight="bold"
                            />
                          </View>
                          <Controller
                            control={control}
                            render={({field: {onChange, onBlur, value}}) => (
                              <RNEInput
                                height={60}
                                placeholder="i.e. to be taken once a week"
                                inputContainerStyle={inputContainerWebStyle}
                                defaultValue={`${prescription.frequency}`}
                                onChangeText={text => {
                                  onChange(text);
                                }}
                                errorMessage={
                                  errors.prescriptions?.[index]?.frequency
                                    ?.message
                                }
                                variant="shadowed"
                              />
                            )}
                            defaultValue={prescription.frequency}
                            name={`prescriptions.${index}.frequency`}
                          />
                        </View>

                        <View style={webStyles.inputContainer}>
                          <View style={webStyles.inputLabel}>
                            <RNEText
                              label="Quantity"
                              variant="button"
                              color="accent"
                              weight="bold"
                            />
                          </View>
                          <Controller
                            control={control}
                            render={({field: {onChange, onBlur, value}}) => (
                              <RNEInput
                                height={60}
                                placeholder="Enter Quantity"
                                inputContainerStyle={inputContainerWebStyle}
                                defaultValue={`${prescription.quantity}`}
                                onChangeText={text => {
                                  onChange(text);
                                }}
                                errorMessage={
                                  errors.prescriptions?.[index]?.quantity
                                    ?.message
                                }
                                variant="shadowed"
                              />
                            )}
                            defaultValue={prescription.quantity}
                            name={`prescriptions.${index}.quantity`}
                          />
                        </View>
                      </View>

                      <View style={webStyles.rowGroupContainer}>
                        <View style={webStyles.inputContainer}>
                          <View style={webStyles.inputLabel}>
                            <RNEText
                              label="General Instruction"
                              variant="button"
                              color="accent"
                              weight="bold"
                            />
                          </View>
                          <Controller
                            control={control}
                            render={({field: {onChange, onBlur, value}}) => (
                              <RNEInput
                                height={80}
                                multiline={true}
                                placeholder="Instruction"
                                inputContainerStyle={textareaContainerWebStyle}
                                defaultValue={`${prescription.generalInstruction}`}
                                onChangeText={text => {
                                  onChange(text);
                                }}
                                errorMessage={
                                  errors.prescriptions?.[index]
                                    ?.generalInstruction?.message
                                }
                                variant="shadowed"
                              />
                            )}
                            defaultValue={prescription.generalInstruction}
                            name={`prescriptions.${index}.generalInstruction`}
                          />
                        </View>
                      </View>

                      <View style={webStyles.actionButtonContainer}>
                        <View style={webStyles.actionButtonWrapper}>
                          {index === fields.length - 1 ? (
                            <RNEButton
                              height={60}
                              title="Add Inscription"
                              color="primary"
                              style={webStyles.actionButtonStyle}
                              onPress={prependInscription}
                            />
                          ) : (
                            <RNEButton
                              height={60}
                              title="Remove"
                              color="primary"
                              style={webStyles.actionButtonStyle}
                              variant="outlined"
                              onPress={() => remove(index)}
                            />
                          )}
                        </View>
                      </View>
                    </View>
                  );
                })}

                <View style={webStyles.buttonsContainer}>
                  <RNEButton
                    title="Cancel"
                    color="accent"
                    style={webStyles.buttonStyle}
                    variant="outlined"
                    height={60}
                    onPress={navigation.goBack}
                  />
                  <View style={webStyles.rightButtonsContainer}>
                    <View style={webStyles.buttonContainer}>
                      <RNEButton
                        title="Save Draft"
                        color="secondary"
                        style={webStyles.buttonStyle}
                        variant="outlined"
                        height={60}
                        loadingProps={loadingStyle}
                        loading={
                          (addMutation.isLoading || updateMutation.isLoading) &&
                          status === MedicationRequestStatusEnum.DRAFT
                        }
                        onPress={() =>
                          savePrescription(MedicationRequestStatusEnum.DRAFT)
                        }
                      />
                    </View>
                    <RNEButton
                      title="Submit"
                      color="secondary"
                      style={webStyles.buttonStyle}
                      height={60}
                      loading={
                        (addMutation.isLoading || updateMutation.isLoading) &&
                        status === MedicationRequestStatusEnum.COMPLETED
                      }
                      onPress={() =>
                        savePrescription(MedicationRequestStatusEnum.COMPLETED)
                      }
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <KeyboardAvoidingView
          style={{flex: 1}}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0} // adjust based on your header height
        >
          <ScrollView
            onScroll={handleScroll}
            onLayout={e => {
              setScrollViewHeight(e.nativeEvent.layout.height);
            }}
            onContentSizeChange={(contentWidth, contentHeight) => {
              setContentHeight(contentHeight);
            }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: height(5),
            }}>
            <View style={mobileStyles.mainContainer}>
              {/* Logo */}
              <View>
                <Image
                  source={LightAppLogo}
                  style={{width: width(30), height: width(10), opacity: 0.3}}
                  resizeMode="contain"
                />
              </View>

              {/* Body */}
              <View style={{padding: height(2), paddingTop: 0}}>
                <View
                  style={{
                    borderBottomColor: colors.black.transparent23,
                    borderBottomWidth: width(0.4),
                    paddingBottom: height(1.5),
                  }}>
                  <Text
                    style={{
                      fontSize: totalSize(2.2),
                      color: colors.midnightBlue.regular,
                      fontFamily: 'Quicksand-Bold',
                    }}>
                    Rx
                  </Text>
                </View>

                <View
                  style={{
                    marginTop: height(2),
                    borderBottomColor: colors.black.transparent23,
                    borderBottomWidth: width(0.4),
                    paddingBottom: height(1.5),
                  }}>
                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        textAlign: 'right',
                        width: width(20),
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Name:
                    </Text>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {patientData?.humanName
                        ? getFullName(patientData?.humanName)
                        : '-'}
                    </Text>
                  </View>

                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        textAlign: 'right',
                        width: width(20),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Age:
                    </Text>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {patientData?.birthdate
                        ? getAgeFromDOB(patientData?.birthdate)
                        : '-'}
                    </Text>
                  </View>

                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        textAlign: 'right',
                        width: width(20),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Address:
                    </Text>
                    <Text
                      style={{
                        flexWrap: 'wrap',
                        width: width(60),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {patientData?.location ? patientData?.location : '-'}
                    </Text>
                  </View>
                </View>

                <View>
                  <Text
                    style={{
                      fontSize: totalSize(5),
                      color: colors.midnightBlue.regular,
                      fontFamily: 'Quicksand-Bold',
                    }}>
                    Rx
                  </Text>
                </View>

                {fields.map((prescription, index) => {
                  return (
                    <View
                      key={prescription.id}
                      style={mobileStyles.inputContainer}>
                      <Controller
                        control={control}
                        render={({field: {onChange, onBlur, value}}) => (
                          <RNEInput
                            height={300}
                            multiline={true}
                            textAlignVertical="top"
                            inputContainerStyle={inputContainerMobileStyle}
                            inputStyle={inputStyle}
                            placeholder=""
                            defaultValue={`${prescription.generalInstruction}`}
                            onChangeText={text => {
                              onChange(text);
                            }}
                            errorMessage={
                              errors.prescriptions?.[index]?.generalInstruction
                                ?.message
                            }
                          />
                        )}
                        defaultValue={prescription.generalInstruction}
                        name={`prescriptions.${index}.generalInstruction`}
                      />
                    </View>
                  );
                })}

                {/* Signature */}
                <View>
                  {practitionerData?.signature && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        width: width(100),
                        marginBottom: -height(4),
                      }}>
                      <Image
                        style={{
                          width: width(45),
                          height: width(20),
                          marginRight: width(10),
                        }}
                        resizeMode="cover"
                        resizeMethod="resize"
                        source={{uri: practitionerData?.signature}}
                      />
                    </View>
                  )}
                  <View>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.8),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      {practitionerData?.humanName
                        ? getFullName(practitionerData?.humanName)
                        : '-'}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      {practitionerData?.practitionerRole
                        ? getSpecialtiesCommaSeparated(
                            practitionerData?.practitionerRole,
                          )
                        : '-'}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      License No:{' '}
                      {practitionerData?.qualification?.identifier || '-'}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      Issued On: {moment().local().format('MMM DD, YYYY')}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Footer */}
              <View style={mobileStyles.buttonsContainer}>
                {/* <RNEButton
                title="Cancel"
                color="accent"
                variant="outlined"
                onPress={navigation.goBack}
              /> */}
                <View style={mobileStyles.rightButtonsContainer}>
                  <View style={mobileStyles.buttonContainer}>
                    <Pressable
                      onPress={() => {
                        if (isValid) {
                          setConfirmationModalTitle(
                            'Are you sure you want to save this as a draft?',
                          );
                          setIsSaveAttachmentModalVisible(
                            !isSaveAttachmentModalVisible,
                          );
                          setConfirmationModalType(
                            MedicationRequestStatusEnum.DRAFT,
                          );
                        } else {
                          savePrescription(MedicationRequestStatusEnum.DRAFT);
                        }
                      }}>
                      <View
                        style={{
                          padding: totalSize(1),
                          paddingRight: totalSize(3),
                          paddingLeft: totalSize(3),
                          borderColor: colorPalette.secondary,
                          borderWidth: totalSize(0.2),
                          borderRadius: totalSize(6),
                        }}>
                        <Text
                          style={{
                            color: colorPalette.secondary,
                            fontSize: totalSize(1.8),
                            fontFamily: 'Quicksand-SemiBold',
                          }}>
                          Save Draft
                        </Text>
                      </View>
                    </Pressable>
                    {/* <RNEButton
                    title="Save Draft"
                    color="secondary"
                    variant="outlined"
                    style={{
                      paddingLeft: width(6),
                      paddingRight: width(6),
                      height: height(5),
                    }}
                    titleStyle={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingTop: height(0),
                      lineHeight: height(1.8),
                    }}
                    loadingProps={loadingStyle}
                    // loading={
                    //   (addMutation.isLoading || updateMutation.isLoading) &&
                    //   status === MedicationRequestStatusEnum.DRAFT
                    // }
                    onPress={() => {
                      if (isValid) {
                        setConfirmationModalTitle(
                          'Are you sure you want to save this as a draft?',
                        );
                        setIsSaveAttachmentModalVisible(
                          !isSaveAttachmentModalVisible,
                        );
                        setConfirmationModalType(
                          MedicationRequestStatusEnum.DRAFT,
                        );
                      } else {
                        savePrescription(MedicationRequestStatusEnum.DRAFT);
                      }
                    }}
                  /> */}
                  </View>

                  <Pressable
                    onPress={() => {
                      if (isValid) {
                        setConfirmationModalTitle(
                          'Are you sure you want to send this to the patient?',
                        );

                        setIsSaveAttachmentModalVisible(
                          !isSaveAttachmentModalVisible,
                        );
                        setConfirmationModalType(
                          MedicationRequestStatusEnum.COMPLETED,
                        );
                      } else {
                        savePrescription(MedicationRequestStatusEnum.COMPLETED);
                      }
                    }}>
                    <View
                      style={{
                        padding: totalSize(1),
                        paddingRight: totalSize(4.5),
                        paddingLeft: totalSize(4.5),
                        backgroundColor: colorPalette.secondary,
                        borderColor: colorPalette.secondary,
                        borderWidth: totalSize(0.3),
                        borderRadius: totalSize(6),
                      }}>
                      <Text
                        style={{
                          color: colorPalette.white,
                          fontSize: totalSize(1.8),
                          fontFamily: 'Quicksand-SemiBold',
                        }}>
                        Submit
                      </Text>
                    </View>
                  </Pressable>

                  {/* <RNEButton
                  title="Submit"
                  color="secondary"
                  buttonBorderWidth={2}
                  style={{
                    paddingLeft: width(8),
                    paddingRight: width(8),
                    height: height(5),
                  }}
                  titleStyle={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: height(0),
                    lineHeight: height(1.8),
                  }}
                  // loading={
                  //   (addMutation.isLoading || updateMutation.isLoading) &&
                  //   status === MedicationRequestStatusEnum.COMPLETED
                  // }
                  onPress={() => {
                    if (isValid) {
                      setConfirmationModalTitle(
                        'Are you sure you want to send this to the patient?',
                      );

                      setIsSaveAttachmentModalVisible(
                        !isSaveAttachmentModalVisible,
                      );
                      setConfirmationModalType(
                        MedicationRequestStatusEnum.COMPLETED,
                      );
                    } else {
                      savePrescription(MedicationRequestStatusEnum.COMPLETED);
                    }
                  }}
                /> */}
                </View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      )}

      <Overlay
        isVisible={isSaveAttachmentModalVisible}
        overlayStyle={sharedStyles.overlayContainer}>
        <ConsultationCancelConfirmationModal
          // isLoading={isArchiveRequestInProgress}
          title={confirmationModalTitle}
          textStyle={{fontSize: totalSize(2)}}
          primaryBtnText={'Yes'}
          secondaryBtnText={'No'}
          isLoading={addMutation.isLoading || updateMutation.isLoading}
          onConfirm={() => {
            if (confirmationModalType === MedicationRequestStatusEnum.DRAFT) {
              savePrescription(MedicationRequestStatusEnum.DRAFT);
            } else if (
              confirmationModalType === MedicationRequestStatusEnum.COMPLETED
            ) {
              savePrescription(MedicationRequestStatusEnum.COMPLETED);
            }
          }}
          onCancel={() =>
            setIsSaveAttachmentModalVisible(!isSaveAttachmentModalVisible)
          }
        />
      </Overlay>
    </>
  );
};

export default AddPrescriptionView;

const subtitleStyle = {
  fontSize: 16,
  paddingLeft: 24,
};

const inputContainerWebStyle = {
  borderRadius: 20,
};

const textareaContainerWebStyle = {
  borderRadius: 20,
  padding: 20,
};

const textareaContainerMobileStyle = {
  borderRadius: 4,
  padding: 12,
};

const loadingStyle = {
  color: colorPalette.secondary,
};

const webStyles = StyleSheet.create({
  titleContainer: {
    flexDirection: 'row',
    marginHorizontal: 100,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: colorPalette.grey,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 100,
    paddingRight: 100,
    paddingVertical: 60,
  },
  formContainer: {
    backgroundColor: colorPalette.white,
    borderRadius: 20,
    paddingHorizontal: 12,
    marginBottom: 80,
    flexDirection: 'row',
  },
  leftContainer: {
    flexShrink: 0,
    borderRightColor: 'rgba(229, 229, 229, 0.7)',
    borderRightWidth: 1,
    paddingVertical: 60,
    paddingRight: 32,
  },
  subtitleContainer: {
    marginBottom: 40,
    paddingLeft: 12,
  },
  infoContainer: {
    marginBottom: 64,
  },
  labelGroupContainer: {
    width: '100%',
    marginTop: 40,
    marginBottom: 20,
    borderBottomColor: 'rgba(229, 229, 229, 0.5)',
    borderBottomWidth: 1.7,
  },
  rowGroupContainer: {
    flexDirection: 'row',
    marginBottom: 60,
  },
  inputContainer: {
    marginRight: 40,
    marginBottom: 4,
    flex: 1,
  },
  inputLabel: {
    paddingLeft: 24,
    marginBottom: 4,
  },
  actionButtonContainer: {
    alignSelf: 'flex-end',
    marginBottom: 60,
  },
  actionButtonWrapper: {
    marginHorizontal: 20,
  },
  rightContainer: {
    flex: 3,
  },
  buttonContainer: {
    marginRight: 20,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 40,
    marginBottom: 20,
    marginRight: 40,
    flexWrap: 'wrap',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    marginLeft: 20,
    flexWrap: 'wrap',
  },
  buttonStyle: {
    width: 140,
  },
  actionButtonStyle: {
    width: 260,
  },
});

const mobileStyles = StyleSheet.create({
  inputContainer: {
    marginTop: height(2),
    marginBottom: height(2.5),
  },
  mainContainer: {
    width: '100%',
    // flex: 1,
    // height: height(95),
    paddingHorizontal: width(5),
    // paddingBottom: 52,
    paddingTop: height(1),
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconContainer: {
    paddingBottom: 12,
  },
  bodyContainer: {
    paddingVertical: 40,
  },
  buttonContainer: {
    marginRight: width(4),
  },
  buttonsContainer: {
    // position: 'absolute',
    marginTop: height(2),
    // left: 0,
    // right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    // marginLeft: 20,
    flexWrap: 'wrap',
  },
});

const inputContainerMobileStyle = {
  borderRadius: totalSize(2.5),
  paddingTop: height(1),
  paddingBottom: height(2),
};

const inputStyle = {
  lineHeight: height(2),
};

const sharedStyles = StyleSheet.create({
  overlayContainer: {
    borderRadius: 20,
    marginHorizontal: 40,
  },
});
